import React, { useState } from 'react';
import type { ContractorAgreement, ContractorApplicant } from '../../../types/contractorOnboard';
import {
  contractorAgreementPdfObjectUrl,
  downloadContractorAgreementPdf,
} from '../../../utils/contractorAgreementPdf';
import { contractorFullName } from '../../../utils/contractorOnboard';
import type { ContractorOnboardViewerOpen } from '../../../utils/contractorOnboardViewer';

type Props = {
  applicant: ContractorApplicant;
  agreement: ContractorAgreement;
  onOpenPacketDoc?: (doc: ContractorOnboardViewerOpen) => void;
};

const ContractorAgreementPdfButtons: React.FC<Props> = ({ applicant, agreement, onOpenPacketDoc }) => {
  const [busy, setBusy] = useState<'view' | 'download' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const resolveUrl = () => contractorAgreementPdfObjectUrl(agreement);

  const view = async () => {
    setBusy('view');
    setError(null);
    try {
      const url = await resolveUrl();
      if (onOpenPacketDoc) {
        onOpenPacketDoc({
          url,
          title: `${contractorFullName(applicant) || 'Contractor'} — 1099 agreement`,
          documentId: `contractor-onboard:${applicant.id}:agreement:${agreement.id}`,
          kind: 'pdf',
          mimeType: 'application/pdf',
          leadName: contractorFullName(applicant) || undefined,
        });
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (e) {
      setError((e as Error).message || 'Could not open the signed agreement');
    } finally {
      setBusy(null);
    }
  };

  const download = async () => {
    setBusy('download');
    setError(null);
    try {
      await downloadContractorAgreementPdf(agreement);
    } catch (e) {
      setError((e as Error).message || 'Could not download the signed agreement');
    } finally {
      setBusy(null);
    }
  };

  return (
    <span className="crew-pooled-summary-actions">
      <button
        type="button"
        className="pm-btn pm-btn-ghost"
        disabled={busy !== null}
        onClick={() => void view()}
      >
        {busy === 'view' ? 'Opening…' : 'View agreement'}
      </button>
      <button
        type="button"
        className="pm-btn pm-btn-ghost"
        disabled={busy !== null}
        onClick={() => void download()}
      >
        {busy === 'download' ? 'Downloading…' : 'Download PDF'}
      </button>
      {error && <span className="people-hiring-resume-error">{error}</span>}
    </span>
  );
};

export default ContractorAgreementPdfButtons;
